import { CoreEvent, eventNames, useCoreEvents } from '@carvana/core-events';
import { useCallback, useEffect, useState } from 'react';

type SoftpullModalOpenPayload = {
  isAddCoSigner: boolean;
  isEditCoSigner: boolean;
  landingHeading?: string;
  landingSubheading?: string;
  showLogo?: boolean;
};

type OwnProps = {
  isAddCoApplicantModalOpen: boolean;
  isEditCoApplicantModalOpen: boolean;
  isSoftPullModalOpen: boolean;
  landingHeading?: string;
  landingSubheading?: string;
  showLogo?: boolean;
  setAddCoApplicantModalOpen: (isOpen: boolean) => void;
  setEditCoApplicantModalOpen: (isOpen: boolean) => void;
  setSoftPullModalOpen: (isOpen: boolean) => void;
};

export default function useToggleModal({
  isAddCoApplicantModalOpen,
  isEditCoApplicantModalOpen,
  isSoftPullModalOpen,
  landingHeading,
  landingSubheading,
  showLogo,
  setAddCoApplicantModalOpen,
  setEditCoApplicantModalOpen,
  setSoftPullModalOpen
}: OwnProps) {
  const [isReady, setReady] = useState(false);

  useCoreEvents(
    eventNames.softpullModal.ready,
    useCallback(() => setReady(true), [])
  );

  useEffect(() => {
    if (isReady) return () => {};

    // Failsafe to close the modal if the MFE doesn't signal it is ready within 10 seconds
    const t = setTimeout(() => {
      setAddCoApplicantModalOpen(false);
      setEditCoApplicantModalOpen(false);
      setSoftPullModalOpen(false);
    }, 10000);

    return () => clearTimeout(t);
  }, [
    isReady,
    setAddCoApplicantModalOpen,
    setEditCoApplicantModalOpen,
    setSoftPullModalOpen
  ]);

  useEffect(() => {
    if (!isReady) return;

    if (
      isSoftPullModalOpen ||
      isAddCoApplicantModalOpen ||
      isEditCoApplicantModalOpen
    ) {
      const payload: SoftpullModalOpenPayload = {
        isAddCoSigner: isAddCoApplicantModalOpen,
        isEditCoSigner: isEditCoApplicantModalOpen,
        landingHeading,
        landingSubheading,
        showLogo
      };
      CoreEvent.emit(eventNames.softpullModal.open, payload as any);

      const closeHandler = () => {
        setSoftPullModalOpen(false);
        setAddCoApplicantModalOpen(false);
        setEditCoApplicantModalOpen(false);
      };
      CoreEvent.once(eventNames.softpullModal.close, closeHandler);

      return () => CoreEvent.off(eventNames.softpullModal.close, closeHandler);
    } else {
      CoreEvent.emit(eventNames.softpullModal.close, {});
    }
  }, [
    isAddCoApplicantModalOpen,
    isEditCoApplicantModalOpen,
    isReady,
    isSoftPullModalOpen,
    landingHeading,
    landingSubheading,
    showLogo,
    setAddCoApplicantModalOpen,
    setEditCoApplicantModalOpen,
    setSoftPullModalOpen
  ]);
}
